import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Autocomplete, Box, Button, Checkbox, Divider, FormControl, FormLabel,
    Input, Option, Select, Sheet, Stack, Textarea, Typography,
} from '@mui/joy';
import { useNavigate } from 'react-router';

import { useAdmin } from '../../../AdminContext';
import {
    APIAdminAdsAdvertisers, APIAdminAdsApplicationCreate,
    APIAdminAdsPlacements, adminAdsError,
} from '../../../api_admin_ads';
import { PRICING_LABELS, OBJECTIVE_LABELS, formatMoney } from '../../../components/Ads/adsConstants';

const GEO_MODES = [
    { value: 'all', label: 'Вся Россия' },
    { value: 'cities', label: 'Выбранные города' },
    { value: 'regions', label: 'Выбранные регионы' },
];

const toList = (s) => (s || '').split(',').map((x) => x.trim()).filter(Boolean);

export default function AdminAdsApplicationCreatePage() {
    const navigate = useNavigate();
    const { adminToken: token } = useAdmin();

    // рекламодатель
    const [advMode, setAdvMode] = useState('existing'); // existing | new
    const [advOptions, setAdvOptions] = useState([]);
    const [advSearch, setAdvSearch] = useState('');
    const [advSelected, setAdvSelected] = useState(null);
    const [newAdv, setNewAdv] = useState({ company_name: '', email: '', phone: '' });

    // кампания
    const [campaign, setCampaign] = useState({
        name: '', objective: 'traffic', pricing_model: 'cpc',
        bid_cpc: '', bid_cpm: '', daily_budget: '',
    });
    const [placements, setPlacements] = useState([]);
    const [selectedCodes, setSelectedCodes] = useState([]);

    // таргетинг
    const [targeting, setTargeting] = useState({
        geo_mode: 'all', cities: '', regions: '', age_min: '', age_max: '',
    });

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // словарь площадок
    useEffect(() => {
        if (!token) return;
        APIAdminAdsPlacements(token)
            .then((res) => setPlacements(res?.items || []))
            .catch(() => {});
    }, [token]);

    // поиск рекламодателей
    const searchAdvertisers = useCallback(async (q) => {
        if (!token) return;
        try {
            const res = await APIAdminAdsAdvertisers(token, { search: q || undefined, page_size: 20 });
            setAdvOptions(res?.items || []);
        } catch (e) { /* тихо */ }
    }, [token]);

    useEffect(() => {
        const t = setTimeout(() => searchAdvertisers(advSearch), 300);
        return () => clearTimeout(t);
    }, [advSearch, searchAdvertisers]);

    const availablePlacements = useMemo(
        () => placements.filter(
            (p) => (p.allowed_pricing || ['cpc', 'cpm']).includes(campaign.pricing_model)
        ),
        [placements, campaign.pricing_model],
    );

    const minBidHint = useMemo(() => {
        const field = campaign.pricing_model === 'cpc' ? 'min_cpc' : 'min_cpm';
        const chosen = placements.filter((p) => selectedCodes.includes(p.code));
        if (!chosen.length) return 0;
        return Math.max(...chosen.map((p) => Number(p[field] || 0)));
    }, [placements, selectedCodes, campaign.pricing_model]);

    const toggleCode = (code) => setSelectedCodes((prev) =>
        prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]);

    const validate = () => {
        if (advMode === 'existing' && !advSelected) return 'Выберите рекламодателя';
        if (advMode === 'new' && (!newAdv.company_name.trim() || !newAdv.email.trim()))
            return 'Для нового рекламодателя укажите название и email';
        if (!campaign.name.trim()) return 'Укажите название кампании';
        if (Number(campaign.daily_budget) <= 0) return 'Укажите дневной бюджет';
        const bid = campaign.pricing_model === 'cpc' ? campaign.bid_cpc : campaign.bid_cpm;
        if (Number(bid) <= 0) return 'Укажите ставку';
        if (!selectedCodes.length) return 'Выберите хотя бы одну площадку';
        return null;
    };

    const submit = async () => {
        const v = validate();
        if (v) { setError(v); return; }
        setError(null);
        setSaving(true);
        try {
            const payload = {
                campaign: {
                    name: campaign.name.trim(),
                    objective: campaign.objective,
                    pricing_model: campaign.pricing_model,
                    bid_cpc: campaign.pricing_model === 'cpc' ? Number(campaign.bid_cpc) : 0,
                    bid_cpm: campaign.pricing_model === 'cpm' ? Number(campaign.bid_cpm) : 0,
                    daily_budget: Number(campaign.daily_budget),
                    placements: selectedCodes,
                },
                group: { name: 'Группа 1' },
                targeting: {
                    geo_mode: targeting.geo_mode,
                    cities: targeting.geo_mode === 'cities' ? toList(targeting.cities) : [],
                    regions: targeting.geo_mode === 'regions' ? toList(targeting.regions) : [],
                    age_min: targeting.age_min ? Number(targeting.age_min) : null,
                    age_max: targeting.age_max ? Number(targeting.age_max) : null,
                },
            };
            if (advMode === 'existing') payload.advertiser_id = advSelected.id;
            else payload.advertiser = {
                company_name: newAdv.company_name.trim(),
                email: newAdv.email.trim(),
                phone: newAdv.phone.trim(),
            };

            const res = await APIAdminAdsApplicationCreate(token, payload);
            navigate(`/admin/ads/applications/${res.campaign.id}`);
        } catch (e) {
            setError(adminAdsError(e, 'Не удалось создать заявку'));
        } finally {
            setSaving(false);
        }
    };

    const bidField = campaign.pricing_model === 'cpc' ? 'bid_cpc' : 'bid_cpm';

    return (
        <Box sx={{ maxWidth: 820 }}>
            <Typography level="h4" sx={{ mb: 2 }}>Новая заявка на рекламу</Typography>
            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Рекламодатель */}
            <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', mb: 2 }}>
                <Typography level="title-md" sx={{ mb: 1 }}>Рекламодатель</Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                    <Button size="sm" variant={advMode === 'existing' ? 'solid' : 'outlined'}
                            onClick={() => setAdvMode('existing')}>Существующий</Button>
                    <Button size="sm" variant={advMode === 'new' ? 'solid' : 'outlined'}
                            onClick={() => setAdvMode('new')}>Новый</Button>
                </Stack>

                {advMode === 'existing' ? (
                    <Autocomplete
                        placeholder="Поиск по названию или email"
                        options={advOptions}
                        value={advSelected}
                        onChange={(_, v) => setAdvSelected(v)}
                        onInputChange={(_, v) => setAdvSearch(v)}
                        getOptionLabel={(o) => `${o.company_name} (${o.email})`}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                    />
                ) : (
                    <Stack spacing={1.5}>
                        <FormControl>
                            <FormLabel>Название компании</FormLabel>
                            <Input value={newAdv.company_name}
                                   onChange={(e) => setNewAdv({ ...newAdv, company_name: e.target.value })} />
                        </FormControl>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                            <FormControl sx={{ flex: 1 }}>
                                <FormLabel>Email</FormLabel>
                                <Input type="email" value={newAdv.email}
                                       onChange={(e) => setNewAdv({ ...newAdv, email: e.target.value })} />
                            </FormControl>
                            <FormControl sx={{ flex: 1 }}>
                                <FormLabel>Телефон</FormLabel>
                                <Input value={newAdv.phone}
                                       onChange={(e) => setNewAdv({ ...newAdv, phone: e.target.value })} />
                            </FormControl>
                        </Stack>
                    </Stack>
                )}
            </Sheet>

            {/* Кампания */}
            <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', mb: 2 }}>
                <Typography level="title-md" sx={{ mb: 1.5 }}>Кампания</Typography>
                <Stack spacing={1.5}>
                    <FormControl>
                        <FormLabel>Название кампании</FormLabel>
                        <Input value={campaign.name}
                               onChange={(e) => setCampaign({ ...campaign, name: e.target.value })} />
                    </FormControl>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Цель</FormLabel>
                            <Select value={campaign.objective}
                                    onChange={(_, v) => setCampaign({ ...campaign, objective: v })}>
                                {Object.entries(OBJECTIVE_LABELS).map(([v, l]) => (
                                    <Option key={v} value={v}>{l}</Option>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Модель оплаты</FormLabel>
                            <Select value={campaign.pricing_model}
                                    onChange={(_, v) => { setCampaign({ ...campaign, pricing_model: v }); setSelectedCodes([]); }}>
                                {Object.entries(PRICING_LABELS).map(([v, l]) => (
                                    <Option key={v} value={v}>{l}</Option>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>
                                Ставка, ₽ ({campaign.pricing_model === 'cpc' ? 'за клик' : 'за 1000 показов'})
                            </FormLabel>
                            <Input type="number" value={campaign[bidField]}
                                   onChange={(e) => setCampaign({ ...campaign, [bidField]: e.target.value })} />
                            {minBidHint > 0 && (
                                <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                                    Не ниже {formatMoney(minBidHint)} ₽ по выбранным площадкам.
                                    Итоговый минимум зависит от охвата — проверится при отправке.
                                </Typography>
                            )}
                        </FormControl>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Дневной бюджет, ₽</FormLabel>
                            <Input type="number" value={campaign.daily_budget}
                                   onChange={(e) => setCampaign({ ...campaign, daily_budget: e.target.value })} />
                        </FormControl>
                    </Stack>

                    <Box>
                        <FormLabel sx={{ mb: 1 }}>Площадки размещения</FormLabel>
                        <Stack spacing={0.5}>
                            {availablePlacements.map((p) => (
                                <Checkbox key={p.code}
                                          label={`${p.name} — мин. ${formatMoney(campaign.pricing_model === 'cpc' ? p.min_cpc : p.min_cpm)} ₽`}
                                          checked={selectedCodes.includes(p.code)}
                                          onChange={() => toggleCode(p.code)} />
                            ))}
                            {availablePlacements.length === 0 && (
                                <Typography level="body-sm" color="neutral">
                                    Нет площадок для выбранной модели оплаты.
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                </Stack>
            </Sheet>

            {/* Таргетинг */}
            <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', mb: 2 }}>
                <Typography level="title-md" sx={{ mb: 0.5 }}>География и аудитория</Typography>
                <Typography level="body-xs" color="neutral" sx={{ mb: 1.5 }}>
                    Тип охвата определяет минимальную ставку и процент партнёра.
                </Typography>
                <Stack spacing={1.5}>
                    <FormControl>
                        <FormLabel>Охват</FormLabel>
                        <Select value={targeting.geo_mode}
                                onChange={(_, v) => setTargeting({ ...targeting, geo_mode: v })}>
                            {GEO_MODES.map((o) => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                        </Select>
                    </FormControl>

                    {targeting.geo_mode === 'cities' && (
                        <FormControl>
                            <FormLabel>Города (через запятую)</FormLabel>
                            <Input placeholder="Москва, Казань"
                                   value={targeting.cities}
                                   onChange={(e) => setTargeting({ ...targeting, cities: e.target.value })} />
                        </FormControl>
                    )}
                    {targeting.geo_mode === 'regions' && (
                        <FormControl>
                            <FormLabel>Регионы (через запятую)</FormLabel>
                            <Input placeholder="Московская область, Татарстан"
                                   value={targeting.regions}
                                   onChange={(e) => setTargeting({ ...targeting, regions: e.target.value })} />
                        </FormControl>
                    )}

                    <Stack direction="row" spacing={1.5}>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Возраст от</FormLabel>
                            <Input type="number" value={targeting.age_min}
                                   onChange={(e) => setTargeting({ ...targeting, age_min: e.target.value })} />
                        </FormControl>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Возраст до</FormLabel>
                            <Input type="number" value={targeting.age_max}
                                   onChange={(e) => setTargeting({ ...targeting, age_max: e.target.value })} />
                        </FormControl>
                    </Stack>
                </Stack>
            </Sheet>

            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={1.5}>
                <Button loading={saving} onClick={submit}>Создать заявку</Button>
                <Button variant="outlined" color="neutral"
                        onClick={() => navigate('/admin/ads/applications')}>Отмена</Button>
            </Stack>
            <Typography level="body-xs" color="neutral" sx={{ mt: 1.5 }}>
                После создания добавите объявления с изображениями и отправите заявку на модерацию.
            </Typography>
        </Box>
    );
}